import {
  previewReportDatasource,
  type PreviewReportDatasourceResult,
  type ReportTemplateDatasourceDto,
} from "../../api/reportTemplateService"
import type { PermitDetail } from "modules/permit/api/permitService"

const normalizeKey = (value?: string | null) => String(value || "").trim().toLowerCase()

const formatPreviewValue = (value: unknown) => {
  if (value == null) return ""
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") return JSON.stringify(value)
  const normalized = String(value).trim()
  if (!normalized) return ""
  const lowered = normalized.toLowerCase()
  if (lowered === "null" || lowered === "undefined") return ""
  return normalized
}

export const buildReportDatasourceContext = (
  row: Record<string, unknown> | null | undefined,
  tenantId: string,
  permitDetail?: PermitDetail | null,
): Record<string, string | number | boolean | null> => {
  const context: Record<string, string | number | boolean | null> = {
    tenant_id: tenantId,
  }

  Object.entries(row ?? {}).forEach(([key, value]) => {
    if (value == null) return
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      context[key] = value
    } else {
      context[key] = String(value)
    }
  })

  const permitId = String(row?.__permitId ?? permitDetail?.permitId ?? row?.permit_id ?? "").trim()
  const permitNumber = String(row?.__permitNumber ?? permitDetail?.permitNumber ?? row?.permit_number ?? "").trim()
  if (permitId) context.permit_id = permitId
  if (permitNumber) context.permit_number = permitNumber

  if (permitDetail) {
    context.title = permitDetail.title || null
    context.status = permitDetail.status || null
    context.template_id = permitDetail.templateId || null
    context.template_version_id = permitDetail.templateVersionId || null
    context.planned_start = permitDetail.plannedStart || null
    context.planned_end = permitDetail.plannedEnd || null
    context.requester_id = permitDetail.requesterId || null
    context.requester_name = permitDetail.requesterName || null
  }

  return context
}

export const loadReportDatasourceResults = async ({
  tenantId,
  datasources,
  row,
  permitDetail,
}: {
  tenantId: string
  datasources: ReportTemplateDatasourceDto[]
  row?: Record<string, unknown> | null
  permitDetail?: PermitDetail | null
}): Promise<Record<string, PreviewReportDatasourceResult>> => {
  const context = buildReportDatasourceContext(row, tenantId, permitDetail)
  const activeSources = (datasources ?? []).filter((item) => String(item.objectName || "").trim())

  const entries = await Promise.all(
    activeSources.map(async (datasource) => {
      const result = await previewReportDatasource({
        tenantId,
        sourceType: datasource.sourceType,
        objectName: datasource.objectName,
        cardinality: datasource.cardinality,
        bindings: datasource.bindings ?? [],
        context,
        rowLimit: datasource.rowLimit ?? 20,
      })
      return [datasource.id, result] as const
    }),
  )

  return Object.fromEntries(entries)
}

export const getDatasourceFieldDisplay = (
  datasourceResults: Record<string, PreviewReportDatasourceResult>,
  datasourceId?: string | null,
  fieldKey?: string | null,
): string => {
  const result = datasourceId ? datasourceResults[datasourceId] : null
  const key = String(fieldKey || "").trim()
  if (!result || !key) return ""
  const value =
    result.record?.[key] ??
    result.rows?.[0]?.[key]
  return formatPreviewValue(value)
}

export const getDatasourceTableRows = (
  datasourceResults: Record<string, PreviewReportDatasourceResult>,
  datasourceId?: string | null,
  columns?: string[],
): string[][] => {
  const result = datasourceId ? datasourceResults[datasourceId] : null
  const safeColumns = (columns ?? []).filter(Boolean)
  if (!result || safeColumns.length === 0) return []

  const rows = result.cardinality === "table"
    ? result.rows
    : (result.record ? [result.record] : result.rows.slice(0, 1))

  return rows.map((row) =>
    safeColumns.map((columnKey) => formatPreviewValue(row?.[columnKey])),
  )
}

export const getDatasourceFieldLabel = (
  datasources: ReportTemplateDatasourceDto[],
  datasourceResults: Record<string, PreviewReportDatasourceResult>,
  datasourceId?: string | null,
  fieldKey?: string | null,
): string => {
  const key = String(fieldKey || "").trim()
  if (!datasourceId || !key) return key
  const datasource = datasources.find((item) => item.id === datasourceId)
  const savedField = datasource?.fields?.find((item) => normalizeKey(item.fieldKey) === normalizeKey(key))
  if (savedField?.label) return savedField.label
  const resultField = datasourceResults[datasourceId]?.columns?.find((item) => normalizeKey(item.fieldKey) === normalizeKey(key))
  return resultField?.label || key
}
